import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import {
    ChevronLeft, TrendingUp, HardHat, Package, Wallet,
    Printer, FileText, ShoppingCart, ArrowRight, Eye,
    Banknote, Receipt, ArrowDownCircle, ArrowUpCircle
} from "lucide-react";
import api from "../api/axiosInstance";

const ProjectProfitability = ({ projectId, onBack }) => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(null); // Drill down state

    useEffect(() => {
        const fetchReport = async () => {
            setLoading(true);
            try {
                const res = await api.get(`get_project_profitabilitynew.php?project_id=${projectId}`);
                setReport(res.data.details);
            } catch (err) {
                console.error("Audit Failed:", err);
            } finally {
                setLoading(false);
            }
        };
        if (projectId) fetchReport();
    }, [projectId]);

    if (loading) return <div className="p-20 text-center font-black animate-pulse text-slate-400 uppercase tracking-widest">Aggregating Site Intelligence...</div>;
    if (!report) return <div className="p-20 text-center"><button onClick={onBack}>Back</button></div>;

    const totalCost = report.costs.reduce((acc, curr) => acc + curr.value, 0);
    const profit = report.revenue - totalCost;
    const marginPercent = report.revenue > 0 ? ((profit / report.revenue) * 100).toFixed(1) : 0;

    // Invoice vs Payment Logic
    const invoiceData = report.billing_summary || { total_invoiced: 0, total_received: 0 };
    const outstanding = invoiceData.total_invoiced - invoiceData.total_received;
    const collectionPercent = invoiceData.total_invoiced > 0 ? ((invoiceData.total_received / invoiceData.total_invoiced) * 100).toFixed(0) : 0;

    return (
        <div className="space-y-8 animate-in fade-in duration-700 pb-20">
            {/* Header Actions */}
            <div className="flex justify-between items-center">
                <button onClick={onBack} className="flex items-center gap-2 text-slate-400 font-black text-[10px] uppercase hover:text-slate-900 transition-all">
                    <ChevronLeft size={16} /> Back to Projects
                </button>
                <div className="flex gap-3">
                    <button className="bg-slate-900 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase flex items-center gap-2 shadow-xl hover:bg-indigo-600 transition-all">
                        <FileText size={16} /> Master Audit PDF
                    </button>
                </div>
            </div>

            {/* Financial Health Row: Invoiced vs Received */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white rounded-[40px] p-8 border border-slate-100 shadow-sm">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xs font-black uppercase text-slate-400 tracking-widest flex items-center gap-2">
                            <Banknote size={16} className="text-emerald-500" /> Revenue Collection Status
                        </h3>
                        <span className="text-[10px] font-black bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full uppercase">
                            {collectionPercent}% Collected
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-8">
                        <div className="space-y-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase">Total Invoiced</p>
                            <h4 className="text-2xl font-black text-slate-900">₹{invoiceData.total_invoiced.toLocaleString()}</h4>
                            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-indigo-500 w-full"></div>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase">Actual Received</p>
                            <h4 className="text-2xl font-black text-emerald-600">₹{invoiceData.total_received.toLocaleString()}</h4>
                            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full bg-emerald-500`} style={{ width: `${collectionPercent}%` }}></div>
                            </div>
                        </div>
                    </div>
                    {outstanding > 0 && (
                        <div className="mt-6 p-4 bg-amber-50 rounded-2xl flex items-center justify-between">
                            <span className="text-[10px] font-black text-amber-700 uppercase">Pending from Client</span>
                            <span className="font-black text-amber-700">₹{outstanding.toLocaleString()}</span>
                        </div>
                    )}
                </div>

                {/* Profit Card */}
                <div className={`rounded-[40px] p-8 text-white shadow-2xl flex flex-col justify-center ${profit >= 0 ? "bg-indigo-600" : "bg-rose-600"}`}>
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-70">Net Site Margin</p>
                    <h3 className="text-5xl font-black italic tracking-tighter">₹{profit.toLocaleString()}</h3>
                    <div className="mt-4 flex items-center gap-2 font-black text-xs uppercase bg-white/20 w-fit px-4 py-1 rounded-full">
                        <TrendingUp size={14} /> {marginPercent}% ROI
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Cost Breakdown with Selectable Categories */}
                <div className="bg-white p-8 rounded-[48px] border border-slate-100 shadow-sm h-fit">
                    <h2 className="text-xl font-black italic uppercase mb-6 text-slate-900">Expense Audit</h2>
                    <div className="space-y-3">
                        {[
                            { id: 'labour', name: "Labour", icon: <HardHat size={18} />, color: "text-rose-500", key: "Labour" },
                            { id: 'material', name: "Materials", icon: <ShoppingCart size={18} />, color: "text-orange-500", key: "Direct Site Buys" },
                            { id: 'petty', name: "Petty Cash", icon: <Wallet size={18} />, color: "text-emerald-500", key: "Petty Cash" },
                            { id: 'vendor', name: "Vendors", icon: <Package size={18} />, color: "text-indigo-500", key: "Vendor Materials" }
                        ].map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setActiveTab(cat.id)}
                                className={`w-full flex justify-between items-center p-5 rounded-3xl transition-all border-2 ${activeTab === cat.id ? "border-indigo-600 bg-indigo-50" : "border-transparent bg-slate-50 hover:bg-slate-100"}`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className={cat.color}>{cat.icon}</span>
                                    <span className="text-[11px] font-black uppercase text-slate-500">{cat.name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-black text-slate-900 text-sm">₹{(report.costs.find(c => c.name === cat.key)?.value || 0).toLocaleString()}</span>
                                    <ArrowRight size={14} className={activeTab === cat.id ? "text-indigo-600" : "text-slate-300"} />
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Dynamic Drill Down Content */}
                <div className="lg:col-span-2 bg-white p-8 rounded-[48px] border border-slate-100 shadow-sm min-h-[500px]">
                    {!activeTab ? (
                        <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-30 italic">
                            <Eye size={48} />
                            <p className="font-bold uppercase text-xs tracking-widest">Select an expense category <br /> to view detailed audit logs</p>
                        </div>
                    ) : (
                        <div className="animate-in fade-in slide-in-from-top-2">
                            <div className="flex justify-between items-center mb-8">
                                <h3 className="text-2xl font-black italic uppercase tracking-tighter">Detailed {activeTab} Audit</h3>
                                <button onClick={() => setActiveTab(null)} className="text-[10px] font-black text-rose-500 uppercase">Close Detail</button>
                            </div>

                            <div className="overflow-hidden rounded-3xl border border-slate-50">
                                <table className="w-full text-left">
                                    <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400">
                                        <tr>
                                            <th className="p-4">Date</th>
                                            <th className="p-4">Description</th>
                                            <th className="p-4 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 text-xs font-bold">
                                        {(report[`${activeTab}_details`] || []).map((item, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50 transition-all">
                                                <td className="p-4 text-slate-400 font-mono">{item.date}</td>
                                                <td className="p-4 uppercase">{item.description || item.name}</td>
                                                <td className="p-4 text-right font-black">₹{item.amount.toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectProfitability;